import React, { useState } from 'react';
import { Link, Typography } from '@mui/material';
import DrugInfoDialog from './DrugInfoDialog';

interface DinLinkProps {
  din: string | undefined | null;
  /** Fallback text when DIN is missing */
  fallback?: string;
  /** Typography variant */
  variant?: 'body1' | 'body2' | 'caption' | 'subtitle2';
  /** Font weight */
  fontWeight?: number | string;
  /** Additional sx */
  sx?: Record<string, unknown>;
}

/**
 * Renders a DIN as a clickable hyperlink that opens the DrugInfoDialog.
 * Drop-in replacement for plain text DIN display anywhere in the app.
 *
 * Usage:
 *   Before: <Typography variant="body2">{din}</Typography>
 *   After:  <DinLink din={din} variant="body2" />
 */
export default function DinLink({
  din,
  fallback = 'N/A',
  variant = 'body2',
  fontWeight,
  sx,
}: DinLinkProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!din) {
    return (
      <Typography variant={variant} sx={sx}>
        {fallback}
      </Typography>
    );
  }

  return (
    <>
      <Link
        component="button"
        variant={variant}
        onClick={(e) => {
          e.stopPropagation();
          setDialogOpen(true);
        }}
        sx={{
          cursor: 'pointer',
          textDecoration: 'none',
          fontWeight: fontWeight,
          '&:hover': { textDecoration: 'underline' },
          ...sx,
        }}
      >
        {din}
      </Link>

      {dialogOpen && (
        <DrugInfoDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          din={din}
        />
      )}
    </>
  );
}
